/**
 * Parameter Corrections Utility
 * Standardized parameter correction calculations for rowing telemetry data
 * Used by all charts to ensure consistent corrections across the application
 */

export interface ParameterInfo {
  id: string;
  session_id: string;
  sweep_oar_length: number | null;
  sweep_oar_inboard: number | null;
  sweep_drive_start: number | null;
  sweep_recovery: number | null;
  scull_oar_length: number | null;
  scull_oar_inboard: number | null;
  scull_drive_start: number | null;
  scull_recovery: number | null;
  impeller_scaling: number | null;
  average_rating_over: number | null;
  created_at: string;
}

/**
 * Standard baseline oar dimensions for parameter corrections
 * Based on World Rowing equipment standards
 */
export const STANDARD_OAR_DIMENSIONS = {
  sweep: {
    totalLength: 376.0, // cm
    inboard: 115.0, // cm
  },
  scull: {
    totalLength: 288.0, // cm
    inboard: 88.0, // cm
  },
} as const;

/**
 * Calculate power correction factors based on oar length differences
 * Enhanced with validation and edge case handling
 */
export function calculatePowerCorrections(parameterInfo: ParameterInfo | null) {
  if (!parameterInfo) {
    return {
      sweepCorrection: 1,
      scullCorrection: 1,
      correction: 1,
      isEnhanced: false,
      correctionType: 'none' as const,
      actualSweepLength: null,
      actualScullLength: null,
      standardSweepLength: STANDARD_OAR_DIMENSIONS.sweep.totalLength,
      standardScullLength: STANDARD_OAR_DIMENSIONS.scull.totalLength,
    };
  }

  // Validate oar lengths (reasonable bounds: 200-500cm total length)
  const validateOarLength = (length: number | null, type: 'sweep' | 'scull'): number | null => {
    if (length === null || length === undefined) return null;

    const minLength = type === 'sweep' ? 300 : 200; // cm
    const maxLength = type === 'sweep' ? 450 : 350; // cm

    if (length < minLength || length > maxLength) {
      console.warn(
        `${type} oar length ${length}cm is outside reasonable bounds (${minLength}-${maxLength}cm). Using standard length.`
      );
      return null;
    }

    return length;
  };

  const validatedSweepLength = validateOarLength(parameterInfo.sweep_oar_length, 'sweep');
  const validatedScullLength = validateOarLength(parameterInfo.scull_oar_length, 'scull');

  // Calculate corrections based on oar length ratios
  const sweepCorrection = validatedSweepLength
    ? validatedSweepLength / STANDARD_OAR_DIMENSIONS.sweep.totalLength
    : 1;

  const scullCorrection = validatedScullLength
    ? validatedScullLength / STANDARD_OAR_DIMENSIONS.scull.totalLength
    : 1;

  // Determine primary correction (sweep takes precedence if both exist)
  let correction = 1;
  let correctionType: 'sweep' | 'scull' | 'none' = 'none';

  if (validatedSweepLength) {
    correction = sweepCorrection;
    correctionType = 'sweep';
  } else if (validatedScullLength) {
    correction = scullCorrection;
    correctionType = 'scull';
  }

  // Additional validation: ensure correction factor is reasonable (0.8 - 1.3)
  if (correction < 0.8 || correction > 1.3) {
    console.warn(
      `Correction factor ${correction.toFixed(3)} is outside reasonable bounds (0.8-1.3). Using standard values.`
    );
    correction = 1;
    correctionType = 'none';
  }

  const isEnhanced = correctionType !== 'none';

  return {
    sweepCorrection,
    scullCorrection,
    correction,
    isEnhanced,
    correctionType,
    actualSweepLength: validatedSweepLength,
    actualScullLength: validatedScullLength,
    // For UI display
    standardSweepLength: STANDARD_OAR_DIMENSIONS.sweep.totalLength,
    standardScullLength: STANDARD_OAR_DIMENSIONS.scull.totalLength,
  };
}

/**
 * Apply power correction to a single value
 */
export function applyPowerCorrection(powerValue: number, correction: number): number {
  return powerValue * correction;
}

/**
 * Apply power correction to an array of values
 */
export function applyPowerCorrections(powerValues: number[], correction: number): number[] {
  return powerValues.map((value) => value * correction);
}

/**
 * Apply power correction to time series data
 */
export function applyPowerCorrectionsToTimeSeries<T extends Record<string, any>>(
  data: T[],
  correction: number,
  powerFields: string[]
): T[] {
  return data.map((point) => {
    const correctedPoint = { ...point } as T;

    powerFields.forEach((field) => {
      if (typeof correctedPoint[field] === 'number') {
        (correctedPoint as any)[field] = applyPowerCorrection(correctedPoint[field], correction);
      }
    });

    return correctedPoint;
  });
}

/**
 * Simple helper for applying corrections to specific power values in objects
 * More type-safe alternative for common use cases
 */
export function correctPowerValues<T>(
  data: T,
  correction: number,
  getValue: (item: T) => number,
  setValue: (item: T, value: number) => T
): T {
  const currentValue = getValue(data);
  const correctedValue = applyPowerCorrection(currentValue, correction);
  return setValue(data, correctedValue);
}

/**
 * Generate correction display text for UI
 */
export function getCorrectionDisplayText(
  corrections: ReturnType<typeof calculatePowerCorrections>
): {
  title: string;
  description: string;
  factor: string;
} | null {
  if (!corrections.isEnhanced) return null;

  const { correctionType, correction } = corrections;

  switch (correctionType) {
    case 'sweep':
      return {
        title: 'Sweep Oar Length Correction Applied',
        description: `Adjusted from standard ${corrections.standardSweepLength}cm`,
        factor: `×${correction.toFixed(3)}`,
      };
    case 'scull':
      return {
        title: 'Scull Oar Length Correction Applied',
        description: `Adjusted from standard ${corrections.standardScullLength}cm`,
        factor: `×${correction.toFixed(3)}`,
      };
    default:
      return null;
  }
}
